import type { LLMTabContentForDispatch } from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';

/**
 * Standard-page domain segment. Mirrors dash-meta's `AssistantDomain`
 * (global-ai-assistant-standard-page-routing.ts) — redeclared here because the
 * FE cannot import from the dash-meta app. Used to build the offer path
 * `/${entityType}/${domain}`.
 */
export type StandardPageDomain =
  | 'compositions'
  | 'pay'
  | 'sentiment'
  | 'transitions'
  | 'postings'
  | 'summary';

/**
 * Canned single-value chart configs for the `get_data_answer` client tool.
 *
 * These replace the `generate_chart_config` LLM call for count/metric
 * questions. The config below is a deterministic chart-SHAPE descriptor only —
 * the entity (company/geography/role) and its filters arrive at render time via
 * the `search_filters` artifact + `entityType`, through the SAME isolated
 * filter scope every inline assistant chart uses
 * (`global-ai-assistant-chart-filter-scope.tsx`). Rendered with
 * `answerAsText: true`, so no chart card is shown — only the figure digest.
 *
 * WHY A PLAIN `bar` WITH NO BREAKDOWN: `isSingleValueAnswer`
 * (`apps/dash-meta/.../global-ai-assistant-standard-page-routing.ts`) treats a
 * config as a single value only when `plot.chartType === 'bar'` with NO
 * `dimensionSource` and NO `secondaryDimension`, and the filters resolve to
 * exactly one primary entity. The FE render path keys the card-less digest off
 * the `answerAsText` flag we pass, so this shape is what makes the answer
 * card-less AND grounded.
 *
 * WHY A SINGLE UNIFIED RECORD: the asked metric and its supporting-context
 * ("companion") metrics are all `DirectAnswerMetric`s and all share the same
 * canned-config shape, so both live in ONE `DIRECT_ANSWER_CONFIGS` record.
 * `DIRECT_ANSWER_COMPANIONS` then just names, per asked metric, which other
 * `DirectAnswerMetric`s to fetch alongside it (fixed budget, max 2). A
 * companion is fetched via `DIRECT_ANSWER_CONFIGS[companion].config` — its own
 * `domain`/`unitHint` are simply unused when it rides as context.
 *
 * ADDING A METRIC (compositions / compensation / sentiment / postings
 * families — the ~100%-of-chartable-metrics roadmap):
 * 1. Add the metric value to the `DirectAnswerMetric` union.
 * 2. Add ONE entry to `DIRECT_ANSWER_CONFIGS`: `config` via
 *    `singleValueConfig('<metric>', '<title>')` (the metric's canonical value —
 *    see the dashboard metric option values, e.g. 'headcount',
 *    'attrition_rate', 'base_salary', 'postings_active'); `domain` = the
 *    metric's standard-page domain (COMPOSITIONS_METRICS → 'compositions',
 *    PAY_METRICS → 'pay', SENTIMENT_METRICS → 'sentiment', POSTINGS_METRICS →
 *    'postings' in global-ai-assistant-standard-page-routing.ts); `unitHint` =
 *    a short unit/formatting note so the model states the figure correctly.
 * 3. Add ONE entry to `DIRECT_ANSWER_COMPANIONS` (up to 2 companions).
 * 4. Add the metric value to the registry's `get_data_answer` metric enum and
 *    one line to its description. No other code changes.
 *
 * TRANSITIONS ARE EXCLUDED: `transitions_inflow` / `transitions_outflow` are
 * NOT bar-safe. `getValidatedPositionedItemsFromLlmContent` keeps chartType
 * `bar` but SILENTLY SWAPS an unsupported metric to `headcount`, so a bar
 * config for a transitions metric would quietly answer headcount. Do NOT add
 * them here. Every metric below is bar-safe for all entities.
 *
 * COMPOSITIONS `salary` IS EXCLUDED (2026-07-07, found by live probe): it is
 * the compositions-family MODELED salary estimate — a different methodology
 * from the pay-family base_salary/total_compensation, so surfacing it next to
 * them produced contradictory-looking figures ("overall salary £144k" under a
 * "base salary £147k") and the model invented explanations for the gap
 * ("including bonuses"). It isn't offered in the custom-dash metric picker
 * either. Pay questions route to the pay family; do not re-add `salary`
 * without a methodology-aware narration plan.
 *
 * POSTINGS RE-ADDED (2026-07-07): the 2026-07-04 exclusion (postings_v3
 * headless request rejected server-side every time) does NOT reproduce
 * through the headless digest host — live probes returned real figures for
 * all five metrics (postings_active 307, postings_new 42, time_to_fill 59.2d,
 * posting_salary $144k). The old failure belonged to the deleted legacy
 * hidden-mount path.
 *
 * SENTIMENT ASPECTS ADDED (2026-07-07): every aspect score in
 * `SENTIMENT_ASPECT_LABELS` rides the same provider as `overall` (verified
 * live: work_life_balance 0.55, business_outlook 0.37) — all are
 * defaultDataType SINGLE + SUPPORTED in libs/core's capability layer, so
 * "how do employees at X feel about <aspect>" is directly answerable.
 * positive_review / negative_review are NOT included (probed empty).
 *
 * Validate any new config with `getValidatedPositionedItemsFromLlmContent` (it
 * must return exactly one item, keeping the requested metric), AND live-probe
 * that the metric actually returns a figure (config-safe ≠ data-safe), before
 * shipping.
 */

/** Grid dims match `DEFAULT_GRID_ROWS` / `DEFAULT_GRID_COLUMNS` (36 × 48). */
const SINGLE_VALUE_GRID = { rows: 36, columns: 48 } as const;

/** Full-width single widget; height is cosmetic (no card renders under answerAsText). */
const SINGLE_VALUE_POSITION = { x: 0, y: 0, width: 48, height: 22 } as const;

/**
 * Sentiment aspect scores, mirroring libs/core's metric catalog (values are
 * the canonical metric ids; labels are the catalog display labels). Every
 * entry is defaultDataType SINGLE + supported, and rides the same sentiment
 * provider as `overall`. MUST stay in sync with the dash-meta registry's
 * `get_data_answer` metric enum (redeclared there — dash-meta cannot import
 * FE app code; same precedent as `StandardPageDomain` above).
 */
export const SENTIMENT_ASPECT_LABELS = {
  business_outlook: 'Business Outlook Sentiment',
  career_growth: 'Career Growth Sentiment',
  compensation: 'Compensation Sentiment',
  benefits: 'Benefits Sentiment',
  healthcare: 'Healthcare Sentiment',
  retirement_pension: 'Retirement & Pension Sentiment',
  childcare: 'Childcare Sentiment',
  education_assistance: 'Education Assistance Sentiment',
  wellness_programs: 'Wellness Programs Sentiment',
  work_life_balance: 'Work-Life Balance Sentiment',
  diversity_and_inclusion: 'Diversity & Inclusion Sentiment',
  leadership: 'Leadership Sentiment',
  culture: 'Culture Sentiment',
  remote_work: 'Remote Work Sentiment',
  onboarding: 'Onboarding Sentiment',
  mentorship: 'Mentorship Sentiment',
  technology: 'Technology Sentiment',
  promotion_opportunities: 'Promotion Opportunities Sentiment',
  paid_time_off: 'Paid Time Off Sentiment',
  transparency: 'Transparency Sentiment',
  efficiency_and_bureaucracy: 'Efficiency & Bureaucracy Sentiment',
  product_and_service_quality: 'Product & Service Quality Sentiment',
  prestige: 'Prestige Sentiment',
  coworkers: 'Coworkers Sentiment',
  mission_and_vision: 'Mission & Vision Sentiment',
  workplace_facilities: 'Workplace Facilities Sentiment',
  training: 'Training Sentiment',
  clients_and_customers: 'Clients & Customers Sentiment',
  company_growth: 'Company Growth Sentiment',
  schedule_flexibility: 'Schedule Flexibility Sentiment',
  job_security: 'Job Security Sentiment',
  overtime: 'Overtime Sentiment',
  family_support: 'Family Support Sentiment',
  commission: 'Commission Sentiment',
  independence: 'Independence Sentiment',
  commute: 'Commute Sentiment',
  safety: 'Safety Sentiment',
  internal_communication: 'Internal Communication Sentiment',
  office_politics: 'Office Politics Sentiment',
  harassment: 'Harassment Sentiment',
  honesty: 'Honesty Sentiment',
  ethics: 'Ethics Sentiment',
  commitment: 'Commitment Sentiment',
  compliance: 'Compliance Sentiment',
  integrity: 'Integrity Sentiment',
} as const;

export type SentimentAspectMetric = keyof typeof SENTIMENT_ASPECT_LABELS;

/** The model-callable metrics for this run (the `metric` enum in the registry). */
export type DirectAnswerMetric =
  | 'headcount'
  | 'growth_rate'
  | 'hiring_rate'
  | 'attrition_rate'
  | 'tenure'
  | 'base_salary'
  | 'total_compensation'
  | 'contractor_pay_rate'
  | 'total_spend_salary'
  | 'total_spend_compensation'
  | 'postings_active'
  | 'postings_new'
  | 'postings_removed'
  | 'posting_salary'
  | 'time_to_fill'
  | 'overall'
  | SentimentAspectMetric;

export type DirectAnswerConfig = {
  /** The chart-shape config fed to the render path (entity-independent). */
  config: LLMTabContentForDispatch;
  /**
   * Standard-page domain for this metric. The executor builds the offer path as
   * `/${entityType}/${domain}` — entityType is a runtime input, so only the
   * domain can be canned here.
   */
  domain: StandardPageDomain;
  /** Short unit/formatting note appended to the tool result so the model states the figure correctly. */
  unitHint: string;
};

/**
 * Build a single-value config for one metric: a plain one-metric bar with NO
 * breakdown (the `isSingleValueAnswer` contract). `title` is the metric's
 * static label — no entity in it (the entity arrives via filters).
 */
function singleValueConfig(
  metric: string,
  title: string
): LLMTabContentForDispatch {
  return {
    items: [
      {
        title,
        position: SINGLE_VALUE_POSITION,
        plot: {
          chartType: 'bar',
          defaultMetricOptions: [metric],
          // NO dimensionSource / secondaryDimension → single-value-capable.
        },
      },
    ],
    grid: SINGLE_VALUE_GRID,
    secondaryFilters: [],
  };
}

const SENTIMENT_SCORE_UNIT_HINT =
  'a score — verify the scale from the digest before stating it; never invent a scale';

/** One generated entry per sentiment aspect — same shape as `overall`. */
const SENTIMENT_ASPECT_CONFIGS = Object.fromEntries(
  (
    Object.entries(SENTIMENT_ASPECT_LABELS) as Array<
      [SentimentAspectMetric, string]
    >
  ).map(([metric, label]) => [
    metric,
    {
      config: singleValueConfig(metric, label),
      domain: 'sentiment' as const,
      unitHint: SENTIMENT_SCORE_UNIT_HINT,
    },
  ])
) as Record<SentimentAspectMetric, DirectAnswerConfig>;

export const DIRECT_ANSWER_CONFIGS: Record<
  DirectAnswerMetric,
  DirectAnswerConfig
> = {
  // Compositions
  headcount: {
    config: singleValueConfig('headcount', 'Headcount'),
    domain: 'compositions',
    unitHint: 'count of people; round large values naturally',
  },
  growth_rate: {
    config: singleValueConfig('growth_rate', 'Growth Rate'),
    domain: 'compositions',
    unitHint: 'a rate in % — express changes in percentage points',
  },
  hiring_rate: {
    config: singleValueConfig('hiring_rate', 'Hiring Rate'),
    domain: 'compositions',
    unitHint: 'a rate in % — express changes in percentage points',
  },
  attrition_rate: {
    config: singleValueConfig('attrition_rate', 'Attrition Rate'),
    domain: 'compositions',
    unitHint: 'a rate in % — express changes in percentage points',
  },
  tenure: {
    config: singleValueConfig('tenure', 'Tenure'),
    domain: 'compositions',
    unitHint: 'years',
  },
  // Pay
  base_salary: {
    config: singleValueConfig('base_salary', 'Base Salary'),
    domain: 'pay',
    unitHint: 'currency; state as approximate',
  },
  total_compensation: {
    config: singleValueConfig('total_compensation', 'Total Compensation'),
    domain: 'pay',
    unitHint: 'currency; state as approximate',
  },
  contractor_pay_rate: {
    config: singleValueConfig('contractor_pay_rate', 'Contractor Pay Rate'),
    domain: 'pay',
    unitHint: 'hourly rate, currency',
  },
  total_spend_salary: {
    config: singleValueConfig('total_spend_salary', 'Total Salary Spend'),
    domain: 'pay',
    unitHint: 'currency total; round naturally',
  },
  total_spend_compensation: {
    config: singleValueConfig(
      'total_spend_compensation',
      'Total Compensation Spend'
    ),
    domain: 'pay',
    unitHint: 'currency total; round naturally',
  },
  // Postings (re-added 2026-07-07 — see doc comment)
  postings_active: {
    config: singleValueConfig('postings_active', 'Active Postings'),
    domain: 'postings',
    unitHint: 'count of currently open job postings',
  },
  postings_new: {
    config: singleValueConfig('postings_new', 'New Postings'),
    domain: 'postings',
    unitHint: 'count of newly created job postings',
  },
  postings_removed: {
    config: singleValueConfig('postings_removed', 'Removed Postings'),
    domain: 'postings',
    unitHint: 'count of removed/filled job postings',
  },
  posting_salary: {
    config: singleValueConfig('posting_salary', 'Posting Salary'),
    domain: 'postings',
    unitHint: 'currency (advertised posting salary); state as approximate',
  },
  time_to_fill: {
    config: singleValueConfig('time_to_fill', 'Time to Fill'),
    domain: 'postings',
    unitHint: 'days to fill a posting; round naturally',
  },
  // Sentiment
  overall: {
    config: singleValueConfig('overall', 'Overall Sentiment'),
    domain: 'sentiment',
    unitHint: SENTIMENT_SCORE_UNIT_HINT,
  },
  ...SENTIMENT_ASPECT_CONFIGS,
};

/**
 * Supporting metrics fetched alongside each asked metric for answer context.
 * Fixed budget (max 2) — this is a bound, not a menu; keeps answers fuller
 * without over-offering. Each companion is itself a `DirectAnswerMetric`,
 * fetched via `DIRECT_ANSWER_CONFIGS[companion].config`.
 */
export const DIRECT_ANSWER_COMPANIONS: Record<
  DirectAnswerMetric,
  DirectAnswerMetric[]
> = {
  headcount: ['growth_rate', 'hiring_rate'],
  growth_rate: ['hiring_rate', 'attrition_rate'],
  hiring_rate: ['growth_rate', 'attrition_rate'],
  attrition_rate: ['hiring_rate', 'tenure'],
  tenure: ['attrition_rate', 'headcount'],
  base_salary: ['total_compensation', 'headcount'],
  total_compensation: ['base_salary', 'headcount'],
  contractor_pay_rate: ['base_salary', 'headcount'],
  total_spend_salary: ['headcount', 'total_spend_compensation'],
  total_spend_compensation: ['headcount', 'total_compensation'],
  postings_active: ['postings_new', 'time_to_fill'],
  postings_new: ['postings_active', 'postings_removed'],
  postings_removed: ['postings_new', 'postings_active'],
  posting_salary: ['postings_active', 'base_salary'],
  time_to_fill: ['postings_active', 'postings_new'],
  overall: ['attrition_rate', 'tenure'],
  // Every aspect gets the same pairing: the overall score anchors the aspect
  // ("0.55 on work-life balance vs 0.50 overall"), attrition adds workforce
  // texture. Uniform on purpose — per-aspect curation is 45 judgment calls
  // with little payoff.
  ...(Object.fromEntries(
    (Object.keys(SENTIMENT_ASPECT_LABELS) as SentimentAspectMetric[]).map(
      (metric) => [metric, ['overall', 'attrition_rate']]
    )
  ) as Record<SentimentAspectMetric, DirectAnswerMetric[]>),
};
